import { createMockCompany, MOCK_COMPANY } from '../company/mock/company.mock';
import { createMockUser, MOCK_USER } from '../user/mock/user.mock';
import type { DemoPersona, DemoPersonaKey } from './demo-persona';

/**
 * Email domain used by every demo persona address. Picked so the
 * Angular `Validators.email` regex still accepts it (`label@domain`)
 * while making it unmistakably synthetic on screen.
 */
const DEMO_EMAIL_DOMAIN = 'demo';

const personaEmail = (label: string): string => `${label}@${DEMO_EMAIL_DOMAIN}`;

/**
 * Canonical persona registry consumed by the `demo` build.
 *
 * The `admin` persona reuses {@link MOCK_USER} / {@link MOCK_COMPANY}
 * verbatim so existing fixtures, e2e flows and CRUD seeds (which all
 * key off `id: 1` / `company_id: 1`) keep working when no explicit
 * persona has been picked yet.
 *
 * The remaining personas live at ids `101..104` to stay clear of the
 * `1..3` range used by `createMockCompanyList(3)` and the rest of the
 * mock fixtures.
 */
export const DEMO_PERSONAS: Record<DemoPersonaKey, DemoPersona> = {
  admin: {
    key: 'admin',
    email: personaEmail('admin'),
    label: 'Admin',
    user: MOCK_USER,
    company: MOCK_COMPANY,
  },
  'supplier.owner': {
    key: 'supplier.owner',
    email: personaEmail('supplier.owner'),
    label: 'Supplier · Owner',
    user: createMockUser({
      id: 101,
      company_id: 1,
      type: 'supplier',
      email: personaEmail('supplier.owner'),
      team_role: 'owner',
    }),
    company: createMockCompany({
      id: 1,
      name: 'Demo Supplier Team (owner)',
      type: 'supplier',
      status: 'paid',
    }),
  },
  'supplier.manager': {
    key: 'supplier.manager',
    email: personaEmail('supplier.manager'),
    label: 'Supplier · Manager',
    user: createMockUser({
      id: 102,
      company_id: 1,
      type: 'supplier',
      email: personaEmail('supplier.manager'),
      team_role: 'manager',
    }),
    company: createMockCompany({
      id: 1,
      name: 'Demo Supplier Team (manager)',
      type: 'supplier',
      status: 'paid',
    }),
  },
  'supplier.viewer': {
    key: 'supplier.viewer',
    email: personaEmail('supplier.viewer'),
    label: 'Supplier · Viewer',
    user: createMockUser({
      id: 103,
      company_id: 1,
      type: 'supplier',
      email: personaEmail('supplier.viewer'),
      team_role: 'viewer',
    }),
    company: createMockCompany({
      id: 1,
      name: 'Demo Supplier Team (viewer)',
      type: 'supplier',
      status: 'paid',
    }),
  },
  'supplier.paid': {
    key: 'supplier.paid',
    email: personaEmail('supplier.paid'),
    label: 'Supplier · paid',
    user: createMockUser({
      id: 101,
      company_id: 101,
      type: 'supplier',
      email: personaEmail('supplier.paid'),
      team_role: 'owner',
    }),
    company: createMockCompany({
      id: 101,
      name: 'Demo Supplier (paid)',
      type: 'supplier',
      status: 'paid',
    }),
  },
  'supplier.free': {
    key: 'supplier.free',
    email: personaEmail('supplier.free'),
    label: 'Supplier · free',
    user: createMockUser({
      id: 102,
      company_id: 102,
      type: 'supplier',
      email: personaEmail('supplier.free'),
    }),
    company: createMockCompany({
      id: 102,
      name: 'Demo Supplier (free)',
      type: 'supplier',
      status: 'free',
    }),
  },
  'client.paid': {
    key: 'client.paid',
    email: personaEmail('client.paid'),
    label: 'Buyer side · paid',
    user: createMockUser({
      id: 103,
      company_id: 103,
      type: 'buyer',
      email: personaEmail('client.paid'),
    }),
    company: createMockCompany({
      id: 103,
      name: 'Demo Buyer (paid)',
      type: 'buyer',
      status: 'paid',
    }),
  },
  'client.free': {
    key: 'client.free',
    email: personaEmail('client.free'),
    label: 'Buyer side · free',
    user: createMockUser({
      id: 104,
      company_id: 104,
      type: 'buyer',
      email: personaEmail('client.free'),
    }),
    company: createMockCompany({
      id: 104,
      name: 'Demo Buyer (free)',
      type: 'buyer',
      status: 'free',
    }),
  },
  'government.paid': {
    key: 'government.paid',
    email: personaEmail('government.paid'),
    label: 'Government · paid',
    user: createMockUser({
      id: 105,
      company_id: 105,
      type: 'government',
      email: personaEmail('government.paid'),
    }),
    company: createMockCompany({
      id: 105,
      name: 'Ministry of Industry (Demo)',
      type: 'government',
      status: 'paid',
    }),
  },
  'government.free': {
    key: 'government.free',
    email: personaEmail('government.free'),
    label: 'Government · free',
    user: createMockUser({
      id: 106,
      company_id: 106,
      type: 'government',
      email: personaEmail('government.free'),
    }),
    company: createMockCompany({
      id: 106,
      name: 'Ministry of Industry (Demo, limited tier)',
      type: 'government',
      status: 'free',
    }),
  },
  'zone.paid': {
    key: 'zone.paid',
    email: personaEmail('zone.paid'),
    label: 'Zone · paid',
    user: createMockUser({
      id: 107,
      company_id: 107,
      type: 'zone',
      email: personaEmail('zone.paid'),
    }),
    company: createMockCompany({
      id: 107,
      name: 'Demo Zone (paid)',
      type: 'zone',
      status: 'paid',
    }),
  },
  'zone.free': {
    key: 'zone.free',
    email: personaEmail('zone.free'),
    label: 'Zone · free',
    user: createMockUser({
      id: 108,
      company_id: 108,
      type: 'zone',
      email: personaEmail('zone.free'),
    }),
    company: createMockCompany({
      id: 108,
      name: 'Demo Zone (free)',
      type: 'zone',
      status: 'free',
    }),
  },
  'moiat@gov.ae': {
    key: 'moiat@gov.ae',
    email: 'moiat@gov.ae',
    label: 'Ministry of Industry',
    user: createMockUser({
      id: 109,
      company_id: 109,
      type: 'government',
      email: 'moiat@gov.ae',
    }),
    company: createMockCompany({
      id: 109,
      name: 'Ministry of Industry',
      type: 'government',
      status: 'paid',
    }),
  },
  'moiat@gov.ea': {
    key: 'moiat@gov.ae',
    email: 'moiat@gov.ae',
    label: 'Ministry of Industry',
    user: createMockUser({
      id: 109,
      company_id: 109,
      type: 'government',
      email: 'moiat@gov.ae',
    }),
    company: createMockCompany({
      id: 109,
      name: 'Ministry of Industry',
      type: 'government',
      status: 'paid',
    }),
  },
  'moita@gov.ae': {
    key: 'moiat@gov.ae',
    email: 'moiat@gov.ae',
    label: 'Ministry of Industry',
    user: createMockUser({
      id: 109,
      company_id: 109,
      type: 'government',
      email: 'moiat@gov.ae',
    }),
    company: createMockCompany({
      id: 109,
      name: 'Ministry of Industry',
      type: 'government',
      status: 'paid',
    }),
  },
  'mioat@gov.ae': {
    key: 'moiat@gov.ae',
    email: 'moiat@gov.ae',
    label: 'Ministry of Industry',
    user: createMockUser({
      id: 109,
      company_id: 109,
      type: 'government',
      email: 'moiat@gov.ae',
    }),
    company: createMockCompany({
      id: 109,
      name: 'Ministry of Industry',
      type: 'government',
      status: 'paid',
    }),
  },
};

/** Default persona used when no explicit demo email has been picked. */
export const DEFAULT_DEMO_PERSONA_KEY: DemoPersonaKey = 'admin';

/** All personas as a list (handy for seeds and UI iteration). */
export const DEMO_PERSONA_LIST: readonly DemoPersona[] =
  Object.values(DEMO_PERSONAS);

/**
 * Resolves a {@link DemoPersona} from a login email. Matching is
 * case-insensitive and ignores leading/trailing whitespace so the
 * demo login form is forgiving of copy/paste.
 *
 * Returns `undefined` for unknown emails so callers can decide whether
 * to fall back to the default persona, surface an error, etc.
 */
export function findDemoPersonaByEmail(
  email: string | null | undefined
): DemoPersona | undefined {
  if (typeof email !== 'string') {
    return undefined;
  }
  const normalized = email.trim().toLowerCase();
  return DEMO_PERSONA_LIST.find(
    (persona) => persona.email.toLowerCase() === normalized
  );
}
